import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ModalDialog from '../ModalDialog';
import PasswordField from '../fields/PasswordField';
import useMultiValidator from '../../hooks/useMultiValidator';
import './_styles_/ChangePasswordPopup.css';
import { onActionKey } from '../../utils';
import PasswordPolicyPopup from './PasswordPolicyPopup';
import {
    currentPasswordValidator,
    passwordValidator,
    repeatedPasswordValidator,
} from '../../validators/passwords';
import {
    getPasswordPolicyFromToken,
    getUserIdFromToken,
} from '../../utils/authentication';
import useAuthentication from '../../hooks/useAuthentication';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';

ChangePasswordPopup.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

function ChangePasswordPopup(props) {
    const {
        onConfirm,
        onCancel,
    } = props;
    const { t } = useTranslation();
    const { accessToken } = useAuthentication();
    const passwordPolicy = getPasswordPolicyFromToken(accessToken);
    const userId = getUserIdFromToken(accessToken);
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [repeatPassword, setRepeatPassword] = useState('');
    const [isPasswordPolicyVisible, setPasswordPolicyVisible] = useState(false);
    const onConfirmHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setOldPassword('');
        setNewPassword('');
        setRepeatPassword('');
        onConfirm(oldPassword, newPassword, repeatPassword);
    };
    const showPasswordPolicy = () => setPasswordPolicyVisible(true);
    const hidePasswordPolicy = () => setPasswordPolicyVisible(false);
    const { validators, isValid } = useMultiValidator(false, {
        oldPassword: currentPasswordValidator(t),
        newPassword: compositeValidator(passwordValidator(t, passwordPolicy, userId), wafValidator(t)),
        repeatPassword: repeatedPasswordValidator(t, newPassword),
    });
    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    const title = t('change-password.title');
    return (
        <ModalDialog onCancel={onCancel}>
            <div className="change-password-popup-dialog" role="dialog" aria-modal>
                <h4>{title}</h4>
                <form className="change-password" onSubmit={onConfirmHandler}>
                    <div className="change-password-input-row">
                        <PasswordField
                            label={t('change-password.old-password')}
                            inputId="change-password-old-password"
                            className="change-password--fields--old-password"
                            value={oldPassword}
                            validator={validators.oldPassword}
                            onChange={setOldPassword}
                            placeholder={t('change-password.placeholder.old-password')}
                        />
                    </div>
                    <div className="change-password-input-row">
                        <PasswordField
                            label={t('change-password.new-password')}
                            inputId="change-password-new-password"
                            className="change-password--fields--new-password"
                            value={newPassword}
                            validator={validators.newPassword}
                            onChange={setNewPassword}
                            placeholder={t('change-password.placeholder.new-password')}
                            autoComplete="new-password"
                            showPasswordStrength
                        />
                    </div>
                    <div className="change-password-input-row">
                        <PasswordField
                            label={t('change-password.repeat-password')}
                            inputId="change-password-repeat-password"
                            className="change-password--fields--repeat-password"
                            value={repeatPassword}
                            validator={validators.repeatPassword}
                            onChange={setRepeatPassword}
                            placeholder={t('change-password.placeholder.repeat-password')}
                            autoComplete="new-password"
                        />
                    </div>
                    <span
                        className="password-policy-link"
                        onClick={showPasswordPolicy}
                        onKeyUp={onActionKey(showPasswordPolicy)}
                        role="button"
                        tabIndex={0}
                    >
                        {t('passwords.show-policy')}
                    </span>
                    <div className="buttons">
                        <button type="submit" className="confirm-change-password-button button-filled-red button" disabled={isDisabled}>{t('button.confirm')}</button>
                        <button type="button" className="cancel-change-password-button button-bordered button" onClick={onCancel}>{t('button.cancel')}</button>
                    </div>
                </form>
            </div>
            {isPasswordPolicyVisible && <PasswordPolicyPopup passwordPolicy={passwordPolicy} hideDialog={hidePasswordPolicy} />}
        </ModalDialog>
    );
}

export default ChangePasswordPopup;
